import { fetchWithAuth } from "./FetchWithAuth";
import { url } from "./url";

/** Flask `scheduling_bp` is mounted at `/schedule` (see SSIE-EMS routes/scheduling_routes.py). */
const base = `${String(url).replace(/\/+$/, "")}/schedule`;

async function request(path, options = {}, auth = true) {
  const endpoint = `${base}${path}`;
  const fetcher = auth ? fetchWithAuth : fetch;
  const response = await fetcher(endpoint, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });
  return response.json();
}

export const schedulingApi = {
  getOptions: () => request("/options"),
  getLecturers: () => request("/lecturers"),

  listClassrooms: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(qs ? `/classrooms?${qs}` : "/classrooms");
  },
  createClassroom: (payload) =>
    request("/classrooms", { method: "POST", body: JSON.stringify(payload) }),
  updateClassroom: (id, payload) =>
    request(`/classrooms/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteClassroom: (id) => request(`/classrooms/${id}`, { method: "DELETE" }),

  listEvents: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(qs ? `/events?${qs}` : "/events");
  },
  getEvent: (id) => request(`/events/${id}`),
  getEventScheduled: (scheduledId) => request(`/events/scheduled/${scheduledId}`),
  updateEventScheduled: (scheduledId, payload) =>
    request(`/events/scheduled/${scheduledId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  createEvent: (payload) =>
    request("/events", { method: "POST", body: JSON.stringify(payload) }),
  createRecurrenceRule: (payload) =>
    request("/recurrence-rules", { method: "POST", body: JSON.stringify(payload) }),
  updateEvent: (id, payload) =>
    request(`/events/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteEvent: (id) => request(`/events/${id}`, { method: "DELETE" }),

  listPendingApprovalEvents: () => request("/events/pending"),
  /** @param {{ page?: number, page_size?: number, search?: string, event_type?: string, sort_by?: string, sort_dir?: string }} params */
  listPendingApprovalEventsPaginated: (params = {}) => {
    const sp = new URLSearchParams();
    if (params.page != null) sp.set("page", String(params.page));
    if (params.page_size != null) sp.set("page_size", String(params.page_size));
    if (params.search) sp.set("search", params.search);
    if (params.event_type) sp.set("event_type", params.event_type);
    if (params.sort_by) sp.set("sort_by", params.sort_by);
    if (params.sort_dir) sp.set("sort_dir", params.sort_dir);
    const qs = sp.toString();
    return request(qs ? `/events/pending/paginated?${qs}` : "/events/pending/paginated");
  },
  approveEvent: (eventId) =>
    request(`/events/${eventId}/approve`, { method: "POST" }),
  rejectEvent: (eventId) =>
    request(`/events/${eventId}/reject`, { method: "POST" }),

  listRescheduleRequests: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(qs ? `/reschedule-requests?${qs}` : "/reschedule-requests");
  },
  approveRescheduleRequest: (requestId) =>
    request(`/reschedule-requests/${requestId}/approve`, { method: "POST" }),
  rejectRescheduleRequest: (requestId) =>
    request(`/reschedule-requests/${requestId}/reject`, { method: "POST" }),

  registerStudent: (eventId, enquiryId) =>
    request(`/events/${eventId}/register`, {
      method: "POST",
      body: JSON.stringify({ enquiry_id: enquiryId }),
    }),
  cancelEnrollment: (eventId, enquiryId) =>
    request(`/events/${eventId}/cancel`, {
      method: "POST",
      body: JSON.stringify({ enquiry_id: enquiryId }),
    }),
  getEventStudents: (eventId, opts = {}) => {
    const sid = opts.scheduledId ?? opts.scheduled_id;
    const qs =
      sid != null && sid !== ""
        ? `?scheduled_id=${encodeURIComponent(String(sid))}`
        : "";
    return request(`/events/${eventId}/students${qs}`);
  },
  searchEnquiriesForEvent: (eventId, q) => {
    const params = new URLSearchParams({ q: q || "" });
    return request(`/events/${eventId}/search-enquiries?${params}`);
  },
  getEnquirySchedule: (enquiryId) => request(`/enquiry/${enquiryId}/schedule`),
  setAttendance: (scheduledId, attendance) =>
    request(`/events/scheduled/${scheduledId}/attendance`, {
      method: "POST",
      body: JSON.stringify({ attendance }),
    }),
  getAttendance: (scheduledId) => request(`/events/scheduled/${scheduledId}/attendance`),

  getPublicRegisterLink: (token, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(qs ? `/register/${token}?${qs}` : `/register/${token}`, {}, false);
  },
  publicEnroll: (token, payload) =>
    request(`/register/${token}/enroll`, {
      method: "POST",
      body: JSON.stringify(payload),
    }, false),

  getLecturerAttendanceContext: (token, timezone) => {
    const params = new URLSearchParams();
    if (timezone) params.set("timezone", timezone);
    const qs = params.toString();
    return request(qs ? `/lecturer-attendance/${token}?${qs}` : `/lecturer-attendance/${token}`, {}, false);
  },
  sendLecturerAttendanceOtp: (token) =>
    request(`/lecturer-attendance/${token}/send-otp`, { method: "POST" }, false),
  verifyLecturerAttendanceOtp: (token, code) =>
    request(`/lecturer-attendance/${token}/verify-otp`, {
      method: "POST",
      body: JSON.stringify({ code }),
    }, false),
  submitLecturerAttendance: (token, attendance) =>
    request(`/lecturer-attendance/${token}/submit`, {
      method: "POST",
      body: JSON.stringify({ attendance }),
    }, false),

  listEnrollmentLinks: () => request("/enrollment-links"),
  createEnrollmentLink: (payload) =>
    request("/enrollment-links", { method: "POST", body: JSON.stringify(payload) }),
};

/**
 * Map schedule event type to Courses.type from the EMS program catalogue:
 * class → courses, seminar → seminars, exam → exam, unit → unit.
 */
export function courseMatchesScheduleEventType(course, scheduleEventType) {
  const et = String(scheduleEventType || "").toLowerCase();
  const ct = String(course?.type || "").trim().toLowerCase();
  if (et === "class") return ct === "courses" || ct === "";
  if (et === "exam") return ct === "exam";
  if (et === "seminar") return ct === "seminars";
  if (et === "unit") return ct === "unit";
  return true;
}

export function filterCoursesByScheduleEventType(courses, scheduleEventType) {
  const list = Array.isArray(courses) ? courses : [];
  return list.filter((c) => courseMatchesScheduleEventType(c, scheduleEventType));
}

export const eventTypeColors = {
  class: "#2563eb",
  exam: "#dc2626",
  seminar: "#7c3aed",
  unit: "#059669",
};

export const eventStatusColors = {
  scheduled: "#2563eb",
  pending_approval: "#f59e0b",
  rejected: "#9ca3af",
  cancelled: "#9ca3af",
  completed: "#059669",
};
